import { EntityId } from "@jackfruit/common"
import { nanoid, PayloadAction } from "@reduxjs/toolkit"
import { SagaIterator } from "redux-saga"
import { call, delay, put, takeEvery } from "redux-saga/effects"
import { ImageAITransformationInputEntity } from "~/interfaces/entities/ImageAITransformationInput"
import { ImageAITransformationOutputEntity } from "~/interfaces/entities/ImageAITransformationOutput"
import { ImageAITransformationResult } from "~/interfaces/entities/ImageAITransformationResult"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { PrinticularApi } from "~/services/PrinticularApi"
import { getToaster, getTranslator } from "../context"
import { actions, ImageAITransformationPayload } from "../process"
import { imageAITransformationInputs } from "../state/imageAITransformationInputs"
import { imageAITransformationOutputs } from "../state/imageAITransformationOutputs"
import { lineItems } from "../state/lineItems"
import { uploads } from "../state/uploads"
import { getApi } from "./api"
import {
  applyAITransformationToUpload,
  getImageAITransformationInput,
  getImageAITransformationOutput,
  updateImageAITransformationStatus,
} from "./imageAITransformations"
import { getAllImageRegionsForLineItem } from "./imageRegion"
import { getLineItem } from "./lineItems"
import { processDuplicateLineItem } from "./processDuplicateLineItem"
import { processRemoveLineItem } from "./processLineItems"
import { getAllUploadsForLineItem } from "./uploads"

export function* watchApplyImageAITransformation() {
  yield takeEvery(
    actions.applyImageAITransformation.type,
    processApplyImageAITransformation
  )
}

function* processApplyImageAITransformation(
  action: PayloadAction<ImageAITransformationPayload>
): SagaIterator {
  const { payload } = action
  const { lineItemId, imageTransformTypeId } = payload
  const { updateOne: updateOneLineItem } = lineItems.actions
  const { updateOne: updateOneUpload } = uploads.actions
  const { addOne: addOneImageAITransformationInput } =
    imageAITransformationInputs.actions
  const { addOne: addOneImageAITransformationOutput } =
    imageAITransformationOutputs.actions

  const newLineItemId: EntityId = yield call(
    processDuplicateLineItem,
    actions.duplicateLineItem({
      lineItemId,
    })
  )

  yield put(
    updateOneLineItem({
      id: newLineItemId,
      changes: {
        isProcessing: true,
      },
    })
  )

  const allUploadsForOriginalLineItem: UploadEntity[] = yield call(
    getAllUploadsForLineItem,
    lineItemId
  )

  const allImageRegions = yield call(getAllImageRegionsForLineItem, lineItemId)
  const allUploadsForNewLineItem: UploadEntity[] = yield call(
    getAllUploadsForLineItem,
    newLineItemId
  )

  // create input and outputs
  const allImageAITransformationInputs: ImageAITransformationInputEntity[] = []
  const allImageAITransformationOutputs: ImageAITransformationOutputEntity[] =
    []

  for (let i = 0; i < allUploadsForOriginalLineItem.length; i++) {
    const originalUpload = allUploadsForOriginalLineItem[i]
    const correspondingNewUpload = allUploadsForNewLineItem[i]
    const correspondingImageRegion = allImageRegions[i]

    const inputId = nanoid()
    const outputId = nanoid()

    const imageAITransformationInputEntity: ImageAITransformationInputEntity = {
      id: inputId,
      imageTransformTypeId,
      lineItemId: newLineItemId,
      imageRegionId: correspondingImageRegion.id,
      outputUploadId: correspondingNewUpload.id,
      uploadId: originalUpload.id,
      status: "pending",
      imageAITransformationOutputId: outputId,
    }

    yield put(
      addOneImageAITransformationInput(imageAITransformationInputEntity)
    )

    const imageAITransformationOutputEntity: ImageAITransformationOutputEntity =
      {
        id: outputId,
        imageTransformTypeId,
        lineItemId: newLineItemId,
        imageRegionId: correspondingImageRegion.id,
        uploadId: correspondingNewUpload.id,
        inputUploadId: originalUpload.id,
        status: "pending",
        originImageUrl: originalUpload.location,
        imageAITransformationInputId: inputId,
      }

    yield put(
      addOneImageAITransformationOutput(imageAITransformationOutputEntity)
    )

    yield put(
      updateOneUpload({
        id: originalUpload.id,
        changes: {
          imageAITransformationInputId: imageAITransformationInputEntity.id,
        },
      })
    )

    yield put(
      updateOneUpload({
        id: correspondingNewUpload.id,
        changes: {
          imageAITransformationOutputId: imageAITransformationOutputEntity.id,
        },
      })
    )

    allImageAITransformationInputs.push(imageAITransformationInputEntity)
    allImageAITransformationOutputs.push(imageAITransformationOutputEntity)
  }

  try {
    for (let i = 0; i < allImageAITransformationInputs.length; i++) {
      const inputId = allImageAITransformationInputs[i].id
      const outputId = allImageAITransformationOutputs[i].id
      yield call(applyAITransformation, {
        imageAITransformationInputId: inputId,
        imageAITransformationOutputId: outputId,
      })
    }

    yield put(
      updateOneLineItem({
        id: newLineItemId,
        changes: {
          isProcessing: false,
        },
      })
    )
  } catch (error) {
    const toaster = getToaster()
    const t = getTranslator()
    toaster({
      title: t(
        "redux.saga.processApplyImageAITransformation.error.rateLimit.title"
      ),
      description: t(
        "redux.saga.processApplyImageAITransformation.error.rateLimit.description"
      ),
      status: "error",
      duration: 9000,
      isClosable: true,
    })

    yield delay(1500)

    // check if the line item is still here
    const lineItemStillPresentInTheCart = yield call(getLineItem, newLineItemId)
    if (lineItemStillPresentInTheCart) {
      yield call(
        processRemoveLineItem,
        actions.removeLineItem({
          lineItemId: newLineItemId,
        })
      )
    }
  }
}

function* applyAITransformation(payload: {
  imageAITransformationInputId: EntityId
  imageAITransformationOutputId: EntityId
}) {
  const { imageAITransformationInputId, imageAITransformationOutputId } =
    payload

  const imageAITransformationInput: ImageAITransformationInputEntity =
    yield call(getImageAITransformationInput, imageAITransformationInputId)
  const imageAITransformationOutput: ImageAITransformationOutputEntity =
    yield call(getImageAITransformationOutput, imageAITransformationOutputId)

  try {
    const api: PrinticularApi = yield call(getApi)

    // create remote transformation
    let imageAITransformResult: ImageAITransformationResult = yield call(
      api.createImageTransformUpload,
      {
        imageTransformTypeId: imageAITransformationInput.imageTransformTypeId,
        imageUrl: imageAITransformationOutput.originImageUrl,
      }
    )

    yield call(updateImageAITransformationStatus, {
      imageAITransformationInputId,
      imageAITransformationOutputId,
      status: "pending",
    })

    while (true) {
      const { retryIn } = imageAITransformResult
      yield delay(retryIn * 1000)
      imageAITransformResult = yield call(api.getImageTransformUpload, {
        imageTransformUploadId: imageAITransformResult.id,
        imageUrl: imageAITransformationOutput.originImageUrl,
      })

      const { status } = imageAITransformResult

      if (status === "completed") {
        yield call(applyAITransformationToUpload, {
          imageAITransformationInputId,
          imageAITransformationOutputId,
          imageAITransformResult,
        })
        // delay status update to let enough time
        // for the image to be preloaded
        yield delay(1500)
      }

      yield call(updateImageAITransformationStatus, {
        imageAITransformationInputId,
        imageAITransformationOutputId,
        status: imageAITransformResult.status,
      })

      // exit on final status
      if (status === "completed") {
        break
      }

      if (status === "failed") {
        throw new Error("Image AI Transformation failed")
      }
    }
  } catch (error) {
    yield call(updateImageAITransformationStatus, {
      imageAITransformationInputId,
      imageAITransformationOutputId,
      status: "failed",
    })

    throw error
  }
}
